import { NavLink } from 'react-router'
import { useTranslation } from '#app/utils/i18n.ts'
import { cn } from '#app/utils/misc.tsx'
import { adminNavGroups } from './admin-nav.ts'

/**
 * The admin shell's left sidebar — `lg`+ only; below that the same destinations
 * ride `AdminMenu` (one data module feeds both). Twelve links no longer fit one
 * header row on a 1280px laptop; vertical space is cheap and scrolls, and the
 * announced growth all lands in the «Гроші» group.
 */
export function AdminSidebar() {
	const t = useTranslation()
	return (
		// The alignment inset lives on this wrapper, OUTSIDE the nav's own width,
		// so it ADDS to the column instead of eating it. `w-56` is a border-box
		// width, so while the inset sat on the <nav> itself it came out of the
		// content box: at 1600px the inset is 132px and the 224px column had 92px
		// of content left, which wrapped the longest labels («Підказки
		// кураторам»). Wrapper-plus-inner rather than `box-content` on the nav:
		// nothing else in this codebase flips the box model, and a layout wrapper
		// keeps "where the column starts" (this element's concern) apart from
		// "how wide the nav is" (the nav's).
		//
		// The row this sits in is full-bleed, the header is a `container` — so
		// aligning the sidebar with the logo takes two cases. BELOW 1400px the
		// container has no max-width, so its content starts at its own
		// `padding-inline: 2rem` and plain `pl-8` matches it. FROM 1400px the
		// container caps at `max-width: 1400px` and `margin-inline: auto`
		// centres it, so the logo starts half the leftover width further in —
		// hence the calc. Both numbers (1400, 2rem) come from the `@utility
		// container` rule in app/styles/tailwind.css; they move together.
		<div className="hidden shrink-0 pl-8 min-[1400px]:ps-[calc((100vw-1400px)/2+2rem)] lg:block">
			<nav
				aria-label={t('nav.admin')}
				// `sticky` belongs on the <nav> itself, not on an inner block. Sticky
				// travel comes from the element's CONTAINING BLOCK: the wrapper above
				// is the flex item of the page's `flex flex-1` row, so it stretches to
				// the full row height, while the nav is height:auto. A sticky child of
				// the nav therefore has a containing block exactly its own height —
				// zero travel, and the sidebar scrolls away with the page.
				//
				// gap-7 between groups against gap-0.5 inside one: the separation
				// between groups has to beat the separation between an item and its
				// neighbour by a lot, or the four groups read as one list.
				className="sticky top-6 flex w-56 flex-col gap-7 py-10"
			>
				{adminNavGroups.map((group) => {
					const labelId = `admin-nav-${group.labelKey.replace(/\./g, '-')}`
					return (
						// role="group" + aria-labelledby, NOT a heading: four <h2>s here
						// would open every page's screen-reader heading list ahead of its
						// own <h1>. The group role carries the same grouping semantics
						// without entering the heading outline.
						<div
							key={group.labelKey}
							role="group"
							aria-labelledby={labelId}
							className="flex flex-col gap-0.5"
						>
							{/* Meta-text, not a row: smaller, lighter-weight and
							    wider-tracked than the items, which start indented under it
							    (`ps-5` below against this label's zero inline padding: the
							    label's text edge lines up with the item pills' left edge, so
							    the column reads as two left edges — label+pill, then item
							    text — instead of three). The tone stays the full
							    `text-muted-foreground` token — the obvious next step, `/70`,
							    drops this 11px text to ~4:1 on the light theme, under the
							    4.5:1 WCAG AA floor for text this size; size, weight and
							    tracking carry the "meta" reading instead. `/4` pins the
							    leading: an arbitrary font-size sets no line-height of its
							    own, so the label-to-list rhythm would otherwise inherit
							    whatever body leading happens to be. */}
							<div
								id={labelId}
								className="text-muted-foreground text-[11px]/4 font-medium tracking-wider uppercase"
							>
								{t(group.labelKey)}
							</div>
							<ul className="flex flex-col">
								{group.items.map((item) => (
									<li key={item.to}>
										{/* NavLink (not Link): React Router sets `aria-current="page"`
										    itself, so the active state reaches screen readers, and the
										    visual cue is weight + background — never colour alone */}
										{/* asymmetric padding on purpose: `ps-5` is the indent under
										    the group label, while `pe-2` keeps the text room wide
										    — the row still fills the column, so the hover/active
										    background stays full-width */}
										<NavLink
											to={item.to}
											className={({ isActive }) =>
												cn(
													'hover:bg-muted block rounded-md py-1 ps-5 pe-2 text-sm',
													isActive ? 'bg-accent font-semibold' : 'font-medium',
												)
											}
										>
											{t(item.labelKey)}
										</NavLink>
									</li>
								))}
							</ul>
						</div>
					)
				})}
			</nav>
		</div>
	)
}
